import NoteHtml, { hasNoteContent, noteToPlainText } from '../../../../../components/common/NoteHtml';
import { formatIstDate } from '../../../../../utils/helpers/istDate';

const PAYMENT_MODES = {
    CASH: 'Cash', CHEQUE: 'Cheque', DD: 'Demand Draft', BANK_TRANSFER: 'Bank Transfer',
    UPI: 'UPI', NEFT: 'NEFT', RTGS: 'RTGS', ONLINE: 'Online',
};

const COMPONENT_LABELS = {
    PRINCIPAL: 'Principal',
    INTEREST: 'Interest',
    ARREAR: 'Arrear',
    DEBIT_NOTE_ARREAR: 'Debit Note Arrear',
};

const ReceiptPrintPage = ({ receipt, pageIdx, totalPages, className = '' }) => {
    const society = receipt.society || {};
    const flat = receipt.flat || {};
    const entity = receipt.entity || {};
    const allocations = receipt.invoiceAllocations || [];

    // Current Receipt Note of the receipt's billing settings (sent by the API).
    const receiptNote = receipt.receipt_note_settings?.receipt_notes || '';
    const showReceiptNote = Number(receipt.receipt_note_settings?.show_receipt_note) === 1 && hasNoteContent(receiptNote);

    // Society address
    const primaryAddress = society.addresses?.find(a => a.is_primary === 1) || society.addresses?.[0] || {};
    const societyAddress = [
        primaryAddress.address_line1,
        primaryAddress.address_line2,
        [primaryAddress.city?.name, primaryAddress.state?.name].filter(Boolean).join(', '),
        primaryAddress.pincode ? `- ${primaryAddress.pincode}` : ''
    ].filter(Boolean).join(', ');

    const primaryContact = society.contacts?.find(c => c.is_primary === 1) || society.contacts?.[0] || {};

    // Member name
    const memberName = entity.entity_type === 'I'
        ? `${entity.first_name || ''} ${entity.last_name || ''}`.trim()
        : entity.company_name || '-';

    const formatCurrency = (amount) => {
        const val = parseFloat(amount || 0);
        return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return formatIstDate(dateStr);
    };

    const amount = parseFloat(receipt.amount || 0);
    const tdsAmount = parseFloat(receipt.tds_deducted || 0);
    const totalAllocated = allocations.reduce((sum, a) => sum + parseFloat(a.amount || 0), 0);
    const unallocated = amount - totalAllocated;

    const numberToWords = (num) => {
        if (num === 0) return 'Zero';
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
            'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const convertLessThanThousand = (n) => {
            if (n === 0) return '';
            if (n < 20) return ones[n];
            if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
            return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convertLessThanThousand(n % 100) : '');
        };
        const absNum = Math.abs(Math.round(num));
        if (absNum === 0) return 'Zero';
        let result = '';
        const crore = Math.floor(absNum / 10000000);
        const lakh = Math.floor((absNum % 10000000) / 100000);
        const thousand = Math.floor((absNum % 100000) / 1000);
        const remainder = absNum % 1000;
        if (crore) result += convertLessThanThousand(crore) + ' Crore ';
        if (lakh) result += convertLessThanThousand(lakh) + ' Lakh ';
        if (thousand) result += convertLessThanThousand(thousand) + ' Thousand ';
        if (remainder) result += convertLessThanThousand(remainder);
        return (num < 0 ? 'Minus ' : '') + result.trim() + ' Rupees Only';
    };

    const getStatusStyles = (status) => {
        switch (status) {
            case 'CLEARED': return { background: '#d1fae5', color: '#065f46' };
            case 'PENDING': return { background: '#fef3c7', color: '#92400e' };
            case 'BOUNCED': return { background: '#fee2e2', color: '#991b1b' };
            case 'CANCELLED': return { background: '#f3f4f6', color: '#374151' };
            default: return { background: '#f3f4f6', color: '#374151' };
        }
    };

    const statusStyles = getStatusStyles(receipt.status);

    return (
        <div className={className} style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", fontSize: '12px' }}>

            {/* Header - Society Info + Receipt Number */}
            <div style={{ borderBottom: '3px solid #059669', padding: '20px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ maxWidth: '60%' }}>
                    <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#064e3b', margin: 0, letterSpacing: '-0.3px' }}>
                        Receipt
                    </h1>
                    <p style={{ fontSize: '14px', color: '#059669', fontWeight: 700, margin: '4px 0 0 0' }}>
                        {society.name || ''}
                    </p>
                    {societyAddress && (
                        <p style={{ fontSize: '10px', color: '#6b7280', margin: '4px 0 0 0', lineHeight: '1.4' }}>
                            {societyAddress}
                        </p>
                    )}
                    {society.registration_number && (
                        <p style={{ fontSize: '10px', color: '#6b7280', margin: '2px 0 0 0' }}>
                            Reg. No: {society.registration_number}
                        </p>
                    )}
                    {(primaryContact.phone || primaryContact.mobile) && (
                        <p style={{ fontSize: '10px', color: '#6b7280', margin: '2px 0 0 0' }}>
                            Tel: {primaryContact.phone || ''}{primaryContact.phone && primaryContact.mobile ? ' | ' : ''}{primaryContact.mobile || ''}
                            {primaryContact.email ? ` | ${primaryContact.email}` : ''}
                        </p>
                    )}
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Receipt No.</div>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: '#064e3b', margin: '2px 0' }}>{receipt.receipt_number || '-'}</div>
                    <div style={{
                        display: 'inline-block',
                        padding: '3px 12px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        ...statusStyles
                    }}>
                        {receipt.status}
                    </div>
                    <div style={{ marginTop: '4px' }}>
                        <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: '8px',
                            fontSize: '9px', fontWeight: 600,
                            background: receipt.receipt_type === 'ADVANCE' ? '#ede9fe' : '#ecfdf5',
                            color: receipt.receipt_type === 'ADVANCE' ? '#6d28d9' : '#047857'
                        }}>
                            {receipt.receipt_type === 'ADVANCE' ? 'Advance' : 'Payment'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Date & Payment Info Row */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', fontSize: '11px' }}>
                <div style={{ flex: 1, padding: '10px 28px', borderRight: '1px solid #e5e7eb' }}>
                    <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>Payment Date</span>
                    <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{formatDate(receipt.payment_date)}</div>
                </div>
                <div style={{ flex: 1, padding: '10px 28px', borderRight: '1px solid #e5e7eb' }}>
                    <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>Payment Mode</span>
                    <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{PAYMENT_MODES[receipt.payment_mode] || receipt.payment_mode || '-'}</div>
                </div>
                <div style={{ flex: 1, padding: '10px 28px' }}>
                    <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>Reference No</span>
                    <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{receipt.reference_no || '-'}</div>
                </div>
            </div>

            {/* Received From / Unit Info */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ flex: 1, padding: '14px 28px', borderRight: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Received From</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>{memberName}</div>
                    {entity.phone && (
                        <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '3px' }}>Phone: {entity.phone}</div>
                    )}
                    {entity.email && (
                        <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>Email: {entity.email}</div>
                    )}
                    {entity.gstin_no && (
                        <div style={{ fontSize: '10px', color: '#059669', marginTop: '2px', fontWeight: 600 }}>GSTIN: {entity.gstin_no}</div>
                    )}
                </div>
                <div style={{ flex: 1, padding: '14px 28px' }}>
                    <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Unit Details</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div>
                            <span style={{ fontSize: '10px', color: '#9ca3af' }}>Unit: </span>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>
                                {flat.wing?.title ? `${flat.wing.title}-` : ''}{flat.flat_number || '-'}
                            </span>
                        </div>
                        {flat.flat_type && (
                            <div>
                                <span style={{ fontSize: '10px', color: '#9ca3af' }}>Type: </span>
                                <span style={{ fontSize: '12px', fontWeight: 600, color: '#111827' }}>{flat.flat_type}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Payment Instrument Details (for Cheque/DD) */}
            {(receipt.bank_name || receipt.instrument_date) && (
                <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', fontSize: '11px' }}>
                    {receipt.bank_name && (
                        <div style={{ flex: 1, padding: '10px 28px', borderRight: receipt.instrument_date ? '1px solid #e5e7eb' : 'none' }}>
                            <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>Bank Name</span>
                            <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{receipt.bank_name}</div>
                        </div>
                    )}
                    {receipt.instrument_date && (
                        <div style={{ flex: 1, padding: '10px 28px' }}>
                            <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>Instrument Date</span>
                            <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{formatDate(receipt.instrument_date)}</div>
                        </div>
                    )}
                </div>
            )}

            {/* Allocations Table */}
            {allocations.length > 0 && (
                <div style={{ padding: '0' }}>
                    <div style={{ background: '#f0fdf4', padding: '8px 28px', borderBottom: '1px solid #bbf7d0' }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Allocations</span>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc' }}>
                                <th style={{ textAlign: 'left', padding: '8px 28px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0', width: '30px' }}>#</th>
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>Document</th>
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>Type</th>
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>Charge heads</th>
                                <th style={{ textAlign: 'right', padding: '8px 28px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0', width: '120px' }}>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allocations.map((alloc, idx) => {
                                const docNumber = alloc.invoice_id
                                    ? (alloc.invoice?.document_number || `INV-${alloc.invoice_id}`)
                                    : (alloc.debitNote?.document_number || `DN-${alloc.debit_note_id}`);
                                const docType = alloc.invoice_id ? 'Invoice' : 'Debit Note';
                                const components = alloc.components || [];
                                const billHeads = alloc.invoice?.items || [];
                                return (
                                    <tr key={alloc.allocation_id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '8px 28px', color: '#9ca3af' }}>{idx + 1}</td>
                                        <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1f2937' }}>{docNumber}</td>
                                        <td style={{ padding: '8px 12px', color: '#6b7280' }}>{docType}</td>
                                        <td style={{ padding: '8px 12px' }}>
                                            {/* What the bill is made of, head by head, then how this
                                                payment was applied across principal / interest / arrears. */}
                                            {billHeads.length > 0 && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                    {billHeads.map((item, hIdx) => (
                                                        <div key={hIdx} style={{ fontSize: '10px', color: '#374151' }}>
                                                            {item.tariffHead?.head_name || item.description || 'Charge'}: {formatCurrency(item.total_amount ?? item.base_amount)}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {components.length > 0 && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: billHeads.length > 0 ? '4px' : 0, paddingTop: billHeads.length > 0 ? '4px' : 0, borderTop: billHeads.length > 0 ? '1px dashed #e5e7eb' : 'none' }}>
                                                    {components.map((comp, cIdx) => (
                                                        <div key={cIdx} style={{ fontSize: '10px', color: '#6b7280' }}>
                                                            Paid towards {COMPONENT_LABELS[comp.component] || comp.component}: {formatCurrency(comp.amount)}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {billHeads.length === 0 && components.length === 0 && (
                                                <span style={{ color: '#9ca3af' }}>-</span>
                                            )}
                                        </td>
                                        <td style={{ padding: '8px 28px', textAlign: 'right', fontWeight: 700, color: '#111827' }}>{formatCurrency(alloc.amount)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* TDS Section */}
            {tdsAmount > 0 && (
                <div style={{ borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ background: '#eef2ff', padding: '8px 28px', borderBottom: '1px solid #c7d2fe' }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, color: '#3730a3', textTransform: 'uppercase', letterSpacing: '0.5px' }}>TDS Details</span>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                        <tbody>
                            <tr style={{ borderBottom: '1px solid #eef2ff' }}>
                                <td style={{ padding: '6px 28px', fontWeight: 600, color: '#3730a3', width: '33%' }}>
                                    TDS Amount
                                </td>
                                <td style={{ padding: '6px 12px', color: '#4338ca' }}>
                                    Rate: {receipt.tds_rate || 0}% {receipt.tds_section ? `| Section: ${receipt.tds_section}` : ''}
                                </td>
                                <td style={{ padding: '6px 28px', textAlign: 'right', fontWeight: 700, color: '#3730a3' }}>
                                    {formatCurrency(tdsAmount)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            {/* Summary Section */}
            <div style={{ borderTop: '2px solid #e2e8f0', display: 'flex' }}>
                {/* Left: Amount in words + Remarks */}
                <div style={{ flex: 1, padding: '14px 28px', borderRight: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Amount in Words</div>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', fontStyle: 'italic' }}>
                        {numberToWords(amount)}
                    </div>

                    {/* Remarks - skipped when it is just the receipt note that
                        Create Receipt prefills, which prints below anyway. */}
                    {receipt.remarks && !(showReceiptNote && receipt.remarks.trim() === noteToPlainText(receiptNote).trim()) && (
                        <div style={{ marginTop: '10px' }}>
                            <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Remarks</div>
                            <div style={{ fontSize: '10px', color: '#6b7280', lineHeight: '1.4' }}>{receipt.remarks}</div>
                        </div>
                    )}

                    {/* Receipt Note (Functional Settings -> Bill Notes -> Receipt Note) */}
                    {showReceiptNote && (
                        <div style={{ marginTop: '10px' }}>
                            <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Note</div>
                            <NoteHtml html={receiptNote} style={{ fontSize: '10px', color: '#6b7280', lineHeight: '1.4' }} />
                        </div>
                    )}
                </div>

                {/* Right: Summary breakdown */}
                <div style={{ width: '260px', padding: '14px 28px' }}>
                    {/* Receipt Amount */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                        <span style={{ color: '#6b7280' }}>Receipt Amount</span>
                        <span style={{ fontWeight: 600, color: '#374151' }}>{formatCurrency(amount)}</span>
                    </div>

                    {/* TDS Deducted */}
                    {tdsAmount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                            <span style={{ color: '#4338ca' }}>TDS Deducted</span>
                            <span style={{ fontWeight: 600, color: '#4338ca' }}>-{formatCurrency(tdsAmount)}</span>
                        </div>
                    )}

                    {/* Net Received */}
                    {tdsAmount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px', borderTop: '1px solid #e5e7eb', marginTop: '4px', paddingTop: '6px' }}>
                            <span style={{ fontWeight: 600, color: '#374151' }}>Net Received</span>
                            <span style={{ fontWeight: 700, color: '#374151' }}>{formatCurrency(amount - tdsAmount)}</span>
                        </div>
                    )}

                    {/* Allocated */}
                    {allocations.length > 0 && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px', borderTop: '1px solid #e5e7eb', marginTop: '4px', paddingTop: '6px' }}>
                                <span style={{ color: '#059669' }}>Total Allocated</span>
                                <span style={{ fontWeight: 600, color: '#059669' }}>{formatCurrency(totalAllocated)}</span>
                            </div>
                            {unallocated > 0.01 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                                    <span style={{ color: '#d97706' }}>Unallocated</span>
                                    <span style={{ fontWeight: 600, color: '#d97706' }}>{formatCurrency(unallocated)}</span>
                                </div>
                            )}
                        </>
                    )}

                    {/* Total Amount */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 4px 0', fontSize: '13px', borderTop: '2px solid #064e3b', marginTop: '6px' }}>
                        <span style={{ fontWeight: 700, color: '#064e3b' }}>Total Amount</span>
                        <span style={{ fontWeight: 800, color: '#064e3b' }}>Rs {formatCurrency(amount)}</span>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div style={{ borderTop: '1px solid #e5e7eb', padding: '10px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                <div style={{ fontSize: '9px', color: '#9ca3af' }}>
                    Generated on {formatIstDate(new Date())}
                </div>
                <div style={{ fontSize: '9px', color: '#9ca3af' }}>
                    {society.name} | Receipt
                    {totalPages > 1 ? ` | Page ${pageIdx + 1} of ${totalPages}` : ''}
                </div>
            </div>
        </div>
    );
};

export default ReceiptPrintPage;
